import { R2_WORKER_URL, reissueR2SessionToken } from "@/features/bucket/api";
import { ADMIN_EMAILS, resolveIsAdmin } from "@/shared/config/admin";
import { clampEvent, sanitizePath } from "../lib/sanitize";
import { perfNow, toLocalIso, tzOffsetMin } from "../lib/time";
import { installClicks } from "./clicks";
import {
    ADMIN_RESOLVE_WAIT_MS,
    EARLY_FLUSH_AT,
    EVENT_MAX_BYTES,
    FLUSH_MS,
    HIDDEN_BEACON_MIN_MS,
    IDLE_ROTATE_MS,
    IMPERSONATION_FLUSH_WAIT_MS,
    isEnabled,
    MAX_SESSION_AGE_MS,
} from "./config";
import { installErrors } from "./errors";
import {
    IDENTITY_KEYS,
    blockOwner,
    impersonationActive,
    invalidateIdentity,
    readIdentity,
    retireToken,
} from "./identity";
import { MediaCapture } from "./media";
import { Outbox } from "./outbox";
import { PageTracker } from "./pages";
import { buildHeader, createSession, LogSession, readTab, TabInfo, writeTab } from "./session";
import { Transport } from "./transport";
import type {
    ActivityEvent,
    ActivityType,
    FinalReason,
    Identity,
    LoginMethod,
    MediaDesc,
    OutboxRecord,
    PageRef,
    PlaybackEndReason,
    SessionHeader,
    TrackOptions,
    VirtualPlayback,
} from "./types";

const HINT_TTL_MS = 5 * 60_000;

/**
 * Один внешний слушатель событий журнала — мост в GA4 (shared/lib/analytics).
 * Зовётся после всех правил журнала (выключен, «войти как», отказ сервера):
 * чего журнал не записал, того и аналитика не получит.
 */
export type ActivityEmitListener = (ev: ActivityEvent) => void;
let emitListener: ActivityEmitListener | null = null;
export const setActivityEmitListener = (fn: ActivityEmitListener | null): void => {
    emitListener = fn;
};

const sleep = <T>(ms: number, value: T): Promise<T> =>
    new Promise((resolve) => setTimeout(() => resolve(value), ms));

const identitySig = (id: Identity): string => `${id.email || ""}|${id.token || ""}`;

const navType = (): string | undefined => {
    try {
        const nav = performance.getEntriesByType("navigation")[0] as
            | PerformanceNavigationTiming
            | undefined;
        return nav?.type;
    } catch {
        return undefined;
    }
};

type StartOptions = { routePattern?: (pathname: string) => string };
type InternalOptions = TrackOptions & { page?: PageRef };

/**
 * Журнал действий по всему сайту. Один экземпляр на страницу: сеанс, очередь,
 * отправка и автоматический сбор (страницы, клики, поля, видео, ошибки).
 * Пока start() не вызван или журнал выключен, все методы — пустые.
 */
class ActivityLogImpl {
    private started = false;
    private on = false;
    private routeOf: (pathname: string) => string = () => "";
    private session: LogSession | null = null;
    private everOpened = false;
    private tab: TabInfo | null = null;
    private rotating = false;
    private manualSuspend = false;
    private suspendedAt = 0;
    private serverSuspendSig: string | null = null;
    private loginHint: { method: LoginMethod; at: number } | null = null;
    private logoutNoted: string | null = null;
    private lastHiddenBeacon = 0;
    private hiddenAt: number | null = null;
    private readonly contexts = new Map<string, Record<string, unknown>>();
    private readonly finalCbs = new Set<(why: FinalReason) => void>();
    private readonly adminCache = new Map<string, boolean>();
    private readonly adminInflight = new Map<string, Promise<boolean>>();

    readonly outbox = new Outbox();
    readonly pages = new PageTracker({
        track: (type, data, page) => this.emit(type, data, { page }),
        routeOf: (p) => this.routeOf(p),
    });
    readonly media = new MediaCapture({
        track: (type, data) => this.emit(type, data),
        contexts: () => this.mergedCtx(),
        lastRouteChangeMs: () => this.pages.lastRouteChangeMs,
    });
    readonly transport = new Transport({
        url: `${R2_WORKER_URL}/activity/log`,
        outbox: this.outbox,
        identity: (fresh) => {
            if (fresh) invalidateIdentity();
            const id = readIdentity();
            return this.manualSuspend || this.serverSuspended(id) ? { ...id, suspended: true } : id;
        },
        headerAsync: (rec) => this.headerAsync(rec),
        headerSync: (rec) => this.headerSync(rec),
        onServerSuspend: (owner) => {
            this.serverSuspendSig = identitySig(readIdentity());
            // Токен — сессия «войти как»: всё, что лежит здесь под этим
            // владельцем, записано глазами админа. Не шлём и не храним.
            this.outbox.purgeOwner(owner);
        },
        onStaleSession: (sid) => {
            if (this.session?.root === sid) this.rotate("stale_session");
        },
        reissue: reissueR2SessionToken,
        warn: (msg, extra) => {
            if (process.env.NODE_ENV !== "production") console.warn(msg, extra ?? "");
        },
    });

    enabled(): boolean {
        return this.on;
    }

    start(opts: StartOptions = {}): void {
        if (opts.routePattern) this.routeOf = opts.routePattern;
        if (this.started) return;
        this.started = true;
        if (!isEnabled()) return;
        this.on = true;
        this.tab = readTab();
        const id = readIdentity();
        // Токен без вошедшего человека остался от прошлого входа (выход его не
        // стирает) — тому, кто войдёт следующим, он не принадлежит.
        if (!id.email && id.token && !id.suspended) retireToken(id.token);
        // Гостевую очередь вкладки держим, только пока здесь гость: её заберёт
        // вход в этой же вкладке (возврат из Google, Stripe). Страница, открытая
        // уже под входом, чужие гостевые действия не наследует.
        this.outbox.adoptOrphans({
            email: id.email,
            tabId: this.tab.tab_id,
            prevSessionId: this.tab.last_session_id,
            guests: id.email ? "drop" : "adopt",
        });
        if (!id.suspended) this.openSession("new_page", id.email);
        this.install();
        setInterval(() => this.tick(), FLUSH_MS);
        if (process.env.NODE_ENV !== "production") {
            (window as unknown as Record<string, unknown>).__activityLog = {
                dump: () => ({ session: this.session, outbox: this.outbox.dump() }),
                outbox: () => this.outbox.dump(),
                flush: () => this.flush(),
            };
        }
    }

    track(type: ActivityType, data?: Record<string, unknown>, opts?: TrackOptions): void {
        this.emit(type, data, opts);
    }

    setContext(key: string, ctx: Record<string, unknown> | null): void {
        if (ctx) this.contexts.set(key, ctx);
        else this.contexts.delete(key);
    }

    refreshIdentity(_reason: string): void {
        if (!this.on) return;
        invalidateIdentity();
        const id = readIdentity();
        if (this.serverSuspendSig !== null && this.serverSuspendSig !== identitySig(id)) {
            this.serverSuspendSig = null;
        }
        this.healSuspend(id);
        if (id.suspended || this.manualSuspend || !this.session) return;
        this.syncOwner(id);
        if (id.email && id.token && this.outbox.pendingFor(id.email) > 0) void this.transport.flush();
    }

    hintLogin(method: LoginMethod): void {
        // Под «войти как» вход — это подмена, а не вход человека.
        if (!this.on || impersonationActive()) return;
        this.loginHint = { method, at: Date.now() };
    }

    noteLogout(reason = "logout"): void {
        if (!this.on) return;
        const id = readIdentity(Date.now());
        const s = this.session;
        // Account зовёт noteLogout, а затем logout() — ещё раз; владелец сменится
        // только после того, как ключи сотрут, поэтому помним сеанс.
        if (s && s.owner !== null && !this.isSuspended() && this.logoutNoted !== s.root) {
            this.logoutNoted = s.root;
            this.emit("auth_logout", { reason });
            // Ключи сотрут сразу после этого вызова: токен берём сейчас.
            if (id.email && id.token && id.email === s.owner) {
                void this.transport.flush({ force: true, as: { email: id.email, token: id.token } });
            }
        }
        // r2_session_token выход не стирает.
        retireToken(id.token);
    }

    async suspendForImpersonation(target: string): Promise<void> {
        if (!this.on) return;
        invalidateIdentity();
        const id = readIdentity();
        if (!id.suspended && !this.manualSuspend) {
            // Адрес цели оставляем: это журнал админа и след того, кого он смотрел.
            const t = String(target || "").trim().toLowerCase().slice(0, 120);
            this.emit("admin_impersonate", { phase: "start", target: t });
        }
        this.manualSuspend = true;
        this.suspendedAt = Date.now();
        this.outbox.persistNow();
        if (!id.email || !id.token || id.suspended) return;
        await Promise.race([
            this.transport
                .flush({ force: true, as: { email: id.email, token: id.token } })
                .catch(() => undefined),
            sleep(IMPERSONATION_FLUSH_WAIT_MS, undefined),
        ]);
    }

    resumeAfterImpersonation(): void {
        if (!this.on) return;
        this.manualSuspend = false;
        invalidateIdentity();
        const id = readIdentity();
        if (id.suspended) return;
        if (this.session) this.syncOwner(id);
        this.emit("admin_impersonate", { phase: "end" });
    }

    async flush(o: { final?: boolean; reason?: string } = {}): Promise<void> {
        if (!this.on) return;
        if (o.final) {
            this.outbox.persistNow();
            this.transport.beacon(this.session?.root);
            return;
        }
        await this.transport.flush({ force: true });
    }

    onBeforeFinal(cb: (why: FinalReason) => void): () => void {
        this.finalCbs.add(cb);
        return () => {
            this.finalCbs.delete(cb);
        };
    }

    onRoute(pathname: string, search: string): void {
        if (this.on) this.pages.onRoute(pathname, search);
    }

    endMediaWithin(root: Element | string, reason: PlaybackEndReason): void {
        if (this.on) this.media.endWithin(root, reason);
    }

    virtualMedia(desc: MediaDesc): VirtualPlayback {
        if (this.on) return this.media.virtual(desc);
        const noop = () => undefined;
        return {
            play: noop,
            pause: noop,
            time: noop,
            seeked: noop,
            rate: noop,
            waiting: noop,
            ended: noop,
            end: noop,
        };
    }

    purgeOwner(email: string): void {
        invalidateIdentity();
        const e = ((email || "").trim() || readIdentity().email || "").toLowerCase();
        if (!e) return;
        blockOwner(e);
        this.outbox.purgeOwner(e);
        if (this.session?.owner === e) this.session = null;
    }

    // ---- internals ------------------------------------------------------------

    /**
     * «Войти как» закончили в другой вкладке — resumeAfterImpersonation сюда не
     * дойдёт. Без метки в хранилище подмена бывает лишь те секунды, пока перед
     * ней уходит последняя пачка админа.
     */
    private healSuspend(id: Identity): void {
        if (
            this.manualSuspend &&
            !id.suspended &&
            Date.now() - this.suspendedAt > 5 * IMPERSONATION_FLUSH_WAIT_MS
        ) {
            this.manualSuspend = false;
        }
    }

    private serverSuspended(id: Identity): boolean {
        return this.serverSuspendSig !== null && this.serverSuspendSig === identitySig(id);
    }

    private isSuspended(): boolean {
        if (this.manualSuspend) return true;
        const id = readIdentity();
        return id.suspended || this.serverSuspended(id);
    }

    private mergedCtx(extra?: Record<string, unknown>): Record<string, unknown> | undefined {
        if (this.contexts.size === 0 && !extra) return undefined;
        const out: Record<string, unknown> = {};
        this.contexts.forEach((v) => Object.assign(out, v));
        if (extra) Object.assign(out, extra);
        return Object.keys(out).length > 0 ? out : undefined;
    }

    private currentPage(): PageRef {
        const cur = this.pages.currentRef();
        if (cur) return cur;
        let route = "";
        try {
            route = this.routeOf(window.location.pathname);
        } catch {
            route = "";
        }
        return { path: sanitizePath(window.location.pathname), route };
    }

    private emit(type: ActivityType, data?: Record<string, unknown>, opts?: InternalOptions): void {
        if (!this.on) return;
        try {
            if (this.isSuspended()) return;
            const now = Date.now();
            if (this.session && !this.rotating) {
                if (now - this.session.lastEventMs > IDLE_ROTATE_MS) this.rotate("idle_rotate");
                else if (now - this.session.startedMs > MAX_SESSION_AGE_MS) this.rotate("age");
                else {
                    const id = readIdentity(now);
                    if (id.email !== this.session.owner) this.syncOwner(id);
                }
            }
            if (!this.session) {
                this.openSession(this.everOpened ? "resume" : "new_page", readIdentity().email);
            }
            const s = this.session;
            if (!s) return;
            const d = new Date(now);
            const ctx = this.mergedCtx(opts?.ctx);
            const ev: ActivityEvent = {
                seq: s.seq + 1,
                type,
                t_utc: d.toISOString(),
                t_local: toLocalIso(d),
                tz_offset_min: tzOffsetMin(d),
                perf_ms: perfNow(),
                page: opts?.page || this.currentPage(),
            };
            if (ctx) ev.ctx = ctx;
            if (data) ev.data = data;
            if (s.owner === null) ev.pre_auth = true;
            const { ev: clamped, bytes } = clampEvent(ev, EVENT_MAX_BYTES);
            s.seq = clamped.seq;
            s.lastEventMs = now;
            this.outbox.push(s.root, clamped, bytes);
            // GA4 bridge (shared/lib/analytics): only what the log itself kept.
            if (emitListener) {
                try {
                    emitListener(ev);
                } catch {
                    /* аналитика не должна мешать журналу */
                }
            }
            if (s.owner && (this.outbox.get(s.root)?.events.length || 0) >= EARLY_FLUSH_AT) {
                void this.transport.flush();
            }
        } catch (e) {
            if (process.env.NODE_ENV !== "production") console.warn("activity log:", e);
        }
    }

    private openSession(reason: string, owner: string | null): void {
        const tab = this.tab || readTab();
        const firstOfPage = !this.everOpened;
        const prevSessionId = tab.last_session_id;
        const s = createSession(owner);
        const header = buildHeader(s, tab, { prevSessionId, firstOfPage });
        this.outbox.create(header, owner, tab.tab_id);
        this.session = s;
        this.everOpened = true;
        this.tab = { tab_id: tab.tab_id, last_session_id: s.root };
        writeTab(this.tab);
        if (owner) void this.adminFlag(owner);
        this.emit("session_start", {
            reason,
            nav_type: firstOfPage ? navType() : undefined,
            entry_path: header.entry_path,
            tab_id: header.tab_id,
            prev_session_id: header.prev_session_id,
            referrer_origin: header.referrer_origin,
            utm: header.utm || undefined,
            env: header.env,
            lang: header.lang,
        });
    }

    /**
     * Новый сеанс: простой больше 30 минут, смена владельца, отказ сервера.
     * Сначала закрываем старый (видео, показы, страница), потом открываем новый
     * и заново отмечаем текущую страницу, чтобы у файла был контекст.
     */
    private rotate(reason: string, owner?: string | null): void {
        if (this.rotating) return;
        this.rotating = true;
        try {
            this.runFinal("rotate");
            this.pages.leave("rotate");
            this.session = null;
            this.openSession(reason, owner === undefined ? readIdentity().email : owner);
            this.pages.reopen("rotate");
        } finally {
            this.rotating = false;
        }
    }

    private syncOwner(id: Identity): void {
        const s = this.session;
        if (!s || id.suspended || this.rotating || s.owner === id.email) return;
        const tabId = this.tab?.tab_id || "";
        // Подсказку оставляют все входы этой вкладки; без неё вход был в другой.
        const method = id.email ? this.takeHint() : null;
        if (s.owner === null && id.email && method) {
            // Гость вошёл здесь: сеанс тот же, владелец назначается; подбираем
            // его старые очереди и гостевые очереди этой же вкладки.
            s.owner = id.email;
            this.outbox.adoptOrphans({ email: id.email, tabId, guests: "adopt" });
            const preAuth = this.outbox.assignOwner(id.email);
            void this.adminFlag(id.email);
            this.emit("auth_login", { method, pre_auth_events: preAuth });
            void this.transport.flush({ force: true });
            return;
        }
        this.rotate("identity_change", id.email);
        if (id.email) {
            this.outbox.dropGuests();
            this.outbox.adoptOrphans({ email: id.email, tabId, guests: "drop" });
            this.emit("auth_login", { method: method || "other_tab", pre_auth_events: 0 });
            void this.transport.flush({ force: true });
        }
    }

    private takeHint(): LoginMethod | null {
        const h = this.loginHint;
        this.loginHint = null;
        return h && Date.now() - h.at < HINT_TTL_MS ? h.method : null;
    }

    private runFinal(why: FinalReason): void {
        this.finalCbs.forEach((cb) => {
            try {
                cb(why);
            } catch {
                /* один подписчик не должен сорвать остальных */
            }
        });
        if (why !== "hidden") this.media.onFinal(why);
    }

    private tick(): void {
        invalidateIdentity();
        const id = readIdentity();
        this.healSuspend(id);
        if (id.suspended || this.manualSuspend || this.serverSuspended(id)) return;
        if (this.session && id.email !== this.session.owner) this.syncOwner(id);
        if (id.email && id.token && this.outbox.pendingFor(id.email) > 0) void this.transport.flush();
    }

    private install(): void {
        this.pages.install();
        this.media.install();
        installClicks({
            track: (type, data) => this.emit(type, data),
            noteClick: () => this.pages.noteClick(),
        });
        installErrors({
            track: (type, data) => this.emit(type, data),
            sessionRoot: () => this.session?.root ?? null,
        });

        window.addEventListener("storage", (e) => {
            if (e.key === null || IDENTITY_KEYS.has(e.key)) this.refreshIdentity("storage");
        });
        document.addEventListener("visibilitychange", () => this.onVisibility());
        window.addEventListener("pagehide", () => this.onPageHide());
        window.addEventListener("pageshow", (e) => {
            if ((e as PageTransitionEvent).persisted) this.pages.reopen("bfcache");
        });
    }

    private onVisibility(): void {
        const now = Date.now();
        if (document.visibilityState === "hidden") {
            this.hiddenAt = now;
            this.pages.onVisibility(true);
            this.runFinal("hidden");
            this.emit("visibility", { state: "hidden" });
            this.outbox.persistNow();
            if (now - this.lastHiddenBeacon >= HIDDEN_BEACON_MIN_MS) {
                this.lastHiddenBeacon = now;
                this.transport.beacon(this.session?.root);
            }
        } else {
            this.emit("visibility", {
                state: "visible",
                hidden_ms: this.hiddenAt === null ? undefined : now - this.hiddenAt,
            });
            this.hiddenAt = null;
            this.pages.onVisibility(false);
        }
    }

    private onPageHide(): void {
        this.runFinal("pagehide");
        this.pages.leave("tab_closed");
        this.outbox.persistNow();
        this.transport.beacon(this.session?.root);
    }

    // ---- session header -------------------------------------------------------

    private adminFlag(owner: string): Promise<boolean> {
        const cached = this.adminCache.get(owner);
        if (cached !== undefined) return Promise.resolve(cached);
        let p = this.adminInflight.get(owner);
        if (!p) {
            p = resolveIsAdmin(owner)
                .then((v) => {
                    this.adminCache.set(owner, v);
                    return v;
                })
                .catch(() => ADMIN_EMAILS.includes(owner))
                .finally(() => this.adminInflight.delete(owner));
            this.adminInflight.set(owner, p);
        }
        return p;
    }

    private finalizeHeader(rec: OutboxRecord, isAdmin: boolean | null): SessionHeader {
        const preview = rec.header.preview_as_user;
        return {
            ...rec.header,
            part: rec.part,
            client_dropped: rec.dropped,
            is_admin: isAdmin,
            user: rec.owner ? { is_admin: isAdmin, preview_as_user: preview } : null,
        };
    }

    // Сервер берёт шапку только при первой записи документа, поэтому признак
    // админа выясняем до отправки, но ждём не дольше трёх секунд.
    private async headerAsync(rec: OutboxRecord): Promise<SessionHeader> {
        if (!rec.owner) return this.finalizeHeader(rec, null);
        const owner = rec.owner;
        const flag = await Promise.race([
            this.adminFlag(owner),
            sleep(ADMIN_RESOLVE_WAIT_MS, ADMIN_EMAILS.includes(owner)),
        ]);
        return this.finalizeHeader(rec, flag);
    }

    private headerSync(rec: OutboxRecord): SessionHeader {
        if (!rec.owner) return this.finalizeHeader(rec, null);
        return this.finalizeHeader(
            rec,
            this.adminCache.get(rec.owner) ?? ADMIN_EMAILS.includes(rec.owner),
        );
    }
}

const impl = new ActivityLogImpl();

export const activityLog = {
    start: (opts?: StartOptions) => impl.start(opts),
    enabled: () => impl.enabled(),
    track: (type: ActivityType, data?: Record<string, unknown>, opts?: TrackOptions) =>
        impl.track(type, data, opts),
    setContext: (key: string, ctx: Record<string, unknown> | null) => impl.setContext(key, ctx),
    refreshIdentity: (reason: string) => impl.refreshIdentity(reason),
    hintLogin: (method: LoginMethod) => impl.hintLogin(method),
    noteLogout: (reason?: string) => impl.noteLogout(reason),
    suspendForImpersonation: (target: string) => impl.suspendForImpersonation(target),
    resumeAfterImpersonation: () => impl.resumeAfterImpersonation(),
    flush: (o?: { final?: boolean; reason?: string }) => impl.flush(o),
    onBeforeFinal: (cb: (why: FinalReason) => void) => impl.onBeforeFinal(cb),
    pages: {
        onRoute: (pathname: string, search: string) => impl.onRoute(pathname, search),
    },
    media: {
        endWithin: (root: Element | string, reason: PlaybackEndReason) =>
            impl.endMediaWithin(root, reason),
        virtual: (desc: MediaDesc) => impl.virtualMedia(desc),
    },
    purgeOwner: (email: string) => impl.purgeOwner(email),
};
